import ConfirmDialog from '@components/Common/ConfirmDialog';
import { useToast } from '@components/Common/Toast/ToastProvider';
import axios from 'axios';
import { useCallback, useState } from 'react';

/**
 * What can be done to a source, in one place for the Sources list and the source's workspace.
 *
 * The two used to be one page and a drawer passing callbacks down. Now that the record is its own
 * route, both call this, so Run, Scout, Approve, Pause, Decline and Take down cannot drift into two
 * versions with different confirmations. Status changes and a takedown are Super Admin only; the
 * callers hide them and the BFF refuses them.
 */

export const API = '/api/external-jobs';
/** A source added in the CC before the Scout has found how to read it (the service's name for it). */
export const PENDING_ADAPTER = 'pending_scout';
/** Adapter names that mean "no way to read this site yet": the service refuses to approve them. */
const NO_ADAPTER = new Set([PENDING_ADAPTER, 'none', 'unknown', '']);
const hasAdapter = (adapter?: string | null): boolean => !NO_ADAPTER.has(adapter ?? '');

/** The subset of a source every action needs. */
export interface ActionableSource {
  sourceId: string;
  companyName?: string | null;
  status: string;
  adapter?: string | null;
  careersUrl?: string | null;
  activeJobs?: number | null;
}

/** The BFF forwards the service's own message, which is written for an operator. */
export const errorMessage = (e: any, fallback: string): string =>
  (typeof e?.response?.data?.message === 'string' && e.response.data.message) || fallback;

/** Can be run now: the row's Run action, and the jobs list's empty state. */
export const canRunSource = (s: Pick<ActionableSource, 'status' | 'adapter'>): boolean =>
  (s.status === 'ACTIVE' || s.status === 'NEEDS_REVIEW') && hasAdapter(s.adapter);

/** Can be approved now (the service refuses an adapter it has not got). */
export const canApproveSource = (s: Pick<ActionableSource, 'status' | 'adapter'>): boolean =>
  s.status !== 'ACTIVE' && s.status !== 'DECLINED' && hasAdapter(s.adapter);

/**
 * Why a source in review shows no Approve button, in words a Super Admin can act on. Without it the
 * card offered Decline alone and looked broken (owner, 2026-10-15). Null when Approve is shown.
 */
export const approveHiddenReason = (
  s: Pick<ActionableSource, 'status' | 'adapter'> & { scout?: { feedback?: string[] | null } | null },
): string | null => {
  if (s.status !== 'NEEDS_REVIEW' || canApproveSource(s)) return null;
  const detail = s.scout?.feedback?.find(Boolean);
  return `Approve appears once the Scout finds a job feed it can read on this site.${detail ? ` Last attempt: ${detail}` : ''}`;
};

/** Can be read again by the Scout: anything not declined that has a careers page. */
export const canScoutSource = (s: Pick<ActionableSource, 'status' | 'careersUrl'>): boolean =>
  s.status !== 'DECLINED' && !!s.careersUrl;

export type PendingSourceAction =
  | { kind: 'status'; source: ActionableSource; status: 'ACTIVE' | 'PAUSED' | 'DECLINED' }
  | { kind: 'takedown'; source: ActionableSource }
  | null;

/** The confirmation each action asks for, or null when nothing is pending. */
export const sourceDialog = (pending: PendingSourceAction) => {
  if (!pending) return null;
  const name = pending.source.companyName || pending.source.sourceId;
  if (pending.kind === 'takedown') {
    return {
      title: `Take down ${name}?`,
      body: `Declines the source, cancels its queued work and deletes all ${pending.source.activeJobs ?? 0} of its live jobs from the site now. Use this when the employer asks for their listings to be removed. It cannot be undone: the jobs come back only if the source is approved and run again.`,
      actionLabel: 'Take down',
      confirmText: pending.source.sourceId,
      confirmLabel: 'Source id',
      reasonLabel: 'Reason',
      reasonPlaceholder: 'Who asked, and when',
    };
  }
  if (pending.status === 'ACTIVE') {
    return {
      title: `Approve ${name}?`,
      body: 'The source runs on its daily schedule from now on, starting with a run in the next minute. Check a manual run first if it has not had one.',
      actionLabel: 'Approve',
      actionColor: 'primary' as const,
    };
  }
  return {
    title: `${pending.status === 'PAUSED' ? 'Pause' : 'Decline'} ${name}?`,
    body:
      pending.status === 'PAUSED'
        ? 'Stops the daily runs. Its jobs stay on the site and close on their own if the source stays paused.'
        : pending.source.status === 'NEEDS_REVIEW'
          ? 'Stops the source for good. Its jobs were never shown to candidates and stay hidden.'
          : 'Stops the source for good. Its jobs stay on the site until they close; use Take down to remove them now.',
    actionLabel: pending.status === 'PAUSED' ? 'Pause' : 'Decline',
    reasonLabel: 'Reason',
    reasonPlaceholder: 'Why, for whoever looks at this source next',
  };
};

/**
 * The actions, their toasts and the one confirmation dialog. `onChanged` reloads whatever the
 * caller shows after a change it should reflect (a queued run changes nothing yet, so it does not).
 */
export function useSourceActions(onChanged: () => void) {
  const { showToast } = useToast();
  const [pending, setPending] = useState<PendingSourceAction>(null);
  const [busy, setBusy] = useState(false);

  const queueRun = useCallback(
    async (s: ActionableSource) => {
      try {
        const r = await axios.post(`${API}/sources/${s.sourceId}/run`);
        showToast(r.data?.enqueued === false ? `${s.sourceId} is already queued` : `Run queued for ${s.sourceId}`, 'success');
      } catch (e) {
        showToast(errorMessage(e, 'The run was not queued.'), 'error');
      }
    },
    [showToast],
  );

  const scoutAgain = useCallback(
    async (s: ActionableSource) => {
      try {
        await axios.post(`${API}/sources/${s.sourceId}/scout`);
        showToast(`The Scout will read ${s.companyName || s.sourceId} again`, 'success');
        onChanged();
      } catch (e) {
        showToast(errorMessage(e, 'The Scout was not queued.'), 'error');
      }
    },
    [showToast, onChanged],
  );

  const confirmPending = async (reason?: string) => {
    if (!pending) return;
    setBusy(true);
    try {
      if (pending.kind === 'takedown') {
        const r = await axios.post(`${API}/sources/${pending.source.sourceId}/takedown`, { reason, status: 'DECLINED' });
        showToast(`${pending.source.sourceId} taken down, ${r.data?.jobsPurged ?? 0} jobs removed`, 'success');
      } else {
        await axios.post(`${API}/sources/${pending.source.sourceId}/status`, { status: pending.status, reason });
        showToast(`${pending.source.sourceId} is now ${pending.status.toLowerCase().replace('_', ' ')}`, 'success');
      }
      setPending(null);
      onChanged();
    } catch (e) {
      showToast(errorMessage(e, 'Nothing was changed.'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const config = sourceDialog(pending);
  const dialog = config ? (
    <ConfirmDialog open busy={busy} onCancel={() => setPending(null)} onConfirm={confirmPending} {...config} />
  ) : null;

  return {
    queueRun,
    scoutAgain,
    approve: (s: ActionableSource) => setPending({ kind: 'status', source: s, status: 'ACTIVE' }),
    pause: (s: ActionableSource) => setPending({ kind: 'status', source: s, status: 'PAUSED' }),
    decline: (s: ActionableSource) => setPending({ kind: 'status', source: s, status: 'DECLINED' }),
    takedown: (s: ActionableSource) => setPending({ kind: 'takedown', source: s }),
    dialog,
  };
}
